import { LinearProgress, Typography } from '@mui/material';
import { FC, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { Trans } from 'react-i18next';
import { Card } from '@/features/card';
import { theme } from '@/styles/theme';
import { calculateFinalPrice } from '@/features/summary/helpers';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TvIcon from '@mui/icons-material/Tv';
import StarIcon from '@mui/icons-material/Star';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DateRangeIcon from '@mui/icons-material/DateRange';
import {
  SummaryBody,
  SummaryType,
  useGetSummaryPriceMutation,
} from '@/models/summary';
import { AdDetailsResponse } from '@/models/ad/types';
import type { Zone } from '@/models/ad/types';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import AddCircleIcon from '@mui/icons-material/AddCircle';

type Props = {
  data: AdDetailsResponse;
};

export const AdStats: FC<Props> = ({ data }) => {
  const currentDisplays = data?.stats.displays.current || 0;
  const totalDisplays = data?.stats.displays.total || 0;
  const currentViews = data?.stats.views.current || 0;
  const totalViews = data?.stats.views.total || 0;

  const displaysProgress = getProgressValue(currentDisplays, totalDisplays);
  const viewsProgress = getProgressValue(currentViews, totalViews);

  const [getPrices, { data: prices }] = useGetSummaryPriceMutation();

  const finalPrice = calculateFinalPrice({
    prices,
    type: data.type,
    zones: data.zones.map((it: { zone: Zone }) => it.zone.id) || [],
    displays: data.stats.displays.total,
    views: data.stats.views.total,
  });

  const bonus = useMemo(() => {
    return calculateBonus({
      currentDisplays,
      currentViews,
      totalDisplays,
      totalViews,
    });
  }, [currentDisplays, currentViews, totalDisplays, totalViews]);

  const bonusPercentage = useMemo(() => {
    return (bonus / (totalDisplays || totalViews)) * 100;
  }, [bonus, totalDisplays, totalViews]);

  useEffect(() => {
    getPrices({
      zones: data?.zones.map((it: { zone: Zone }) => it.zone.id) || [],
      type: 'image',
      summaryType: SummaryType.PRICE_SUMMARY,
    });
  }, [data, getPrices]);

  useEffect(() => {
    const payload: SummaryBody = {
      zones: data?.zones.map((it: { zone: Zone }) => it.zone.id),
      type: 'image',
      summaryType: SummaryType.PRICE_SUMMARY,
    };

    if (data?.stats.displays.total) {
      payload.displays = 1000;
    }

    if (data?.stats.views.total) {
      payload.views = 1000;
    }

    if (data?.stats.displays.total || data?.stats.views.total) {
      getPrices(payload);
    }
  }, [data, getPrices]);

  const finalPriceParsed = parseFloat(finalPrice);
  const averageCost = getAverageCost({
    finalPrice: finalPriceParsed,
    totalViews,
    totalDisplays,
    currentViews,
    currentDisplays,
  });

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
      <Card className="flex flex-col justify-between">
        <div className="flex mb-4 justify-between">
          <Trans i18nKey="label.displays" />
          <TvIcon
            color="secondary"
            sx={{
              fontSize: '1.5rem',
            }}
          />
        </div>
        <div className="flex gap-2 justify-between items-end mb-2">
          <Typography
            variant="h5"
            fontWeight="bold"
            color={totalDisplays > 0 ? 'secondary' : 'primary'}
          >
            {data?.stats.displays.current.toLocaleString()}
          </Typography>
          {totalDisplays > 0 && (
            <Typography variant="h6" fontWeight="bold">
              {data?.stats.displays.total.toLocaleString()}
            </Typography>
          )}
        </div>
        <div className="flex items-center mb-2">
          <div className="w-full">
            <LinearProgress
              variant="determinate"
              value={displaysProgress}
              sx={{
                visibility: totalDisplays > 0 ? 'visible' : 'hidden',
                borderRadius: '1rem',
                '& .MuiLinearProgress-barColorPrimary': {
                  backgroundColor: theme.palette.secondary.main,
                },
              }}
            />
          </div>
        </div>
        <div className="flex gap-2 justify-between items-end mb-2">
          <Typography
            variant="caption"
            color={totalDisplays > 0 ? 'secondary' : 'primary'}
          >
            <Trans i18nKey="my_ads.ad_stats_widget.received_displays" />
          </Typography>
          <Typography variant="caption">
            <Trans i18nKey="my_ads.ad_stats_widget.bought_displays" />
          </Typography>
        </div>
      </Card>

      <Card className="flex flex-col justify-between">
        <div className="flex mb-4 justify-between">
          <Trans i18nKey="label.views" />
          <VisibilityIcon
            color="secondary"
            sx={{
              fontSize: '1.5rem',
            }}
          />
        </div>

        <div className="flex gap-2 justify-between items-center mb-2">
          <div className="flex flex-col items-start">
            <Typography
              variant="h5"
              fontWeight="bold"
              color={totalViews > 0 ? 'secondary' : 'primary'}
            >
              {data?.stats.views.current.toLocaleString()}
            </Typography>
            <Typography
              variant="caption"
              color={totalViews > 0 ? 'secondary' : 'primary'}
            >
              <Trans i18nKey="my_ads.ad_stats_widget.received_displays" />
            </Typography>
          </div>
          {totalViews > 0 && (
            <div className="flex flex-col items-end">
              <Typography variant="caption">
                <Trans i18nKey="my_ads.ad_stats_widget.bought_displays" />
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {data?.stats.views.total.toLocaleString()}
              </Typography>
            </div>
          )}
        </div>
        <div className="flex items-center">
          <div className="w-full">
            <LinearProgress
              variant="determinate"
              value={viewsProgress}
              sx={{
                visibility: totalViews > 0 ? 'visible' : 'hidden',
                borderRadius: '1rem',
                '& .MuiLinearProgress-barColorPrimary': {
                  backgroundColor: theme.palette.secondary.main,
                },
              }}
            />
          </div>
        </div>
      </Card>

      <Card className="flex flex-col justify-between relative">
        <div className="flex mb-4 justify-between">
          <Trans i18nKey="my_ads.ad_stats_widget.bonus" />
          <StarIcon
            color="secondary"
            sx={{
              fontSize: '1.5rem',
            }}
          />
        </div>
        <div className="flex gap-2 justify-between items-end mb-4">
          <div className="flex flex-col items-start">
            <div className="flex gap-2">
              <Typography
                variant="h5"
                fontWeight="bold"
                color={totalDisplays > 0 ? 'secondary' : 'primary'}
              >
                {bonus}
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                ({bonusPercentage}%)
              </Typography>
            </div>
            <Typography variant="caption">
              {totalViews ? (
                <Trans i18nKey="my_ads.ad_stats_widget.bonus_views" />
              ) : (
                <Trans i18nKey="my_ads.ad_stats_widget.bonus_displays" />
              )}
            </Typography>
          </div>
        </div>
      </Card>

      <Card className="flex flex-col justify-between">
        <div className="flex mb-4 justify-between">
          {totalViews ? (
            <Trans i18nKey="partner.chart.average_cost_per_view" />
          ) : (
            <Trans i18nKey="partner.chart.average_cost_per_display" />
          )}
          <AttachMoneyIcon
            color="secondary"
            sx={{
              fontSize: '1.5rem',
            }}
          />
        </div>
        <div className="flex gap-2 justify-between items-end mb-4">
          <div className="flex flex-col items-start">
            <div className="flex gap-2">
              <Typography
                variant="h5"
                fontWeight="bold"
                color={totalDisplays > 0 ? 'secondary' : 'primary'}
              >
                {averageCost} <Trans i18nKey="common.label.pln" />
              </Typography>
            </div>
            <Typography variant="caption">
              <Trans i18nKey="my_ads.ad_stats_widget.average_cost_per_display" />
            </Typography>
          </div>
        </div>
      </Card>

      <Card className="flex flex-col justify-between">
        <div className="flex mb-4 justify-between">
          <Trans i18nKey="my_ads.ad_stats_widget.created_date.label" />
          <AddCircleIcon
            color="secondary"
            sx={{
              fontSize: '1,5rem',
            }}
          />
        </div>
        <Typography variant="h5" fontWeight="bold">
          {dayjs(data.createdAt).format('DD.MM.YYYY')}
        </Typography>
        <Typography variant="body1">
          {dayjs(data.createdAt).format('HH:mm:ss')}
        </Typography>
      </Card>

      {data.publishedAt ? (
        <Card className="flex flex-col justify-between">
          <div className="flex mb-4 justify-between">
            <Trans i18nKey="my_ads.ad_stats_widget.publish_date.label" />
            <DateRangeIcon
              color="secondary"
              sx={{
                fontSize: '1.5rem',
              }}
            />
          </div>
          <Typography variant="h5" fontWeight="bold">
            {dayjs(data.publishedAt).format('DD.MM.YYYY')}
          </Typography>
          <Typography variant="body1">
            {dayjs(data.publishedAt).format('HH:mm:ss')}
          </Typography>
        </Card>
      ) : null}

      <Card className="flex flex-col justify-start">
        <div className="flex mb-4 justify-between">
          <Trans i18nKey="my_ads.ad_stats_widget.start_date.label" />
          <FlightTakeoffIcon
            color="secondary"
            sx={{
              fontSize: '1.5rem',
            }}
          />
        </div>
        <Typography variant="h5" fontWeight="bold">
          {dayjs(data.startDate).format('DD.MM.YYYY')}
        </Typography>
      </Card>

      <Card className="flex flex-col justify-start">
        <div className="flex mb-4 justify-between">
          <Trans i18nKey="my_ads.ad_stats_widget.end_date.label" />
          <FlightLandIcon
            color="secondary"
            sx={{
              fontSize: '1.5rem',
            }}
          />
        </div>
        <Typography variant="h5" fontWeight="bold">
          {dayjs(data.endDate).format('DD.MM.YYYY')}
        </Typography>
      </Card>
    </div>
  );
};

const getAverageCost = ({
  finalPrice,
  totalViews,
  totalDisplays,
  currentViews,
  currentDisplays,
}: {
  finalPrice: number;
  totalViews: number;
  totalDisplays: number;
  currentViews: number;
  currentDisplays: number;
}) => {
  let result = 0;

  if (totalViews > 0) {
    result = currentViews > 0 ? finalPrice / currentViews : finalPrice;
  }

  if (totalDisplays > 0) {
    result = currentDisplays > 0 ? finalPrice / currentDisplays : finalPrice;
  }

  // Check for valid numeric result
  if (!isFinite(result) || isNaN(result)) {
    return '0.00';
  }

  return result > 0 ? result.toFixed(2) : result.toFixed(3);
};

const getProgressValue = (current: number, total: number) =>
  (current / total) * 100;

const calculateBonus = ({
  currentDisplays,
  currentViews,
  totalDisplays,
  totalViews,
}: {
  currentViews: number;
  totalViews: number;
  totalDisplays: number;
  currentDisplays: number;
}) => {
  if (totalViews > 0 && currentViews > totalViews) {
    return currentViews - totalViews;
  }

  if (totalDisplays > 0 && currentDisplays > totalDisplays) {
    return currentDisplays - totalDisplays;
  }

  return 0;
};
